/**
 * External dependencies
 */
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import type { HeatmapFrameAction } from '@nab/types';

/**
 * Internal dependencies
 */
import { reactToParentMessages as heatmapReact } from './heatmap';
import { reactToParentMessages as scrollmapReact } from './scrollmap';
import { reactToParentMessages as confettiReact } from './confetti';

import { disableLinks, loadAssets, onPageReady } from './internal/page';
import { hasWordPressData } from './internal/utils';
import {
	isInteractive,
	markPageAsReady,
	markScriptAsReady,
	onWindowResize,
	processResults,
} from './internal/store';

import { domReady } from '../../../public/utils/helpers';

type MaybeHeatmapFrameAction = {
	readonly plugin?: string;
};

function init() {
	if ( ! hasWordPressData( window.parent ) ) {
		console.error( 'Parent window doesn’t have expected API. Bye!' ); // eslint-disable-line
		return;
	}

	window.addEventListener( 'message', reactToParentMessages, false );

	window.addEventListener( 'resize', debounce( onWindowResize, 100 ) );
	onWindowResize();

	markScriptAsReady();
	domReady( loadAssets );
	domReady( disableLinks );
	onPageReady( markPageAsReady );
}

function reactToParentMessages( message: { data: MaybeHeatmapFrameAction } ) {
	const action = message.data;
	if ( ! isHeatmapFrameAction( action ) ) {
		return;
	}
	makeHeatmapInteractive();
	heatmapReact( action );
	scrollmapReact( action );
	confettiReact( action );
}

let hasListeners = false;
const refresh = throttle( processResults, 500 );
function makeHeatmapInteractive() {
	const interactive = isInteractive();
	if ( interactive === hasListeners ) {
		return;
	}

	if ( interactive ) {
		document.addEventListener( 'mousemove', refresh );
		document.addEventListener( 'click', refresh );
		window.addEventListener( 'scroll', refresh );
	} else {
		document.removeEventListener( 'mousemove', refresh );
		document.removeEventListener( 'click', refresh );
		window.removeEventListener( 'scroll', refresh );
	}

	hasListeners = interactive;
}

const isHeatmapFrameAction = (
	action: MaybeHeatmapFrameAction
): action is HeatmapFrameAction => action.plugin === 'nelio-ab-testing';

// =======
// KICKOFF
// =======

init();
